import {
  formatMaltaDistanceFeeLineDisplay,
  MALTA_DISTANCE_FEE_LINE_LABELS,
  MALTA_DISTANCE_FEE_LINE_ORDER,
  MALTA_DISTANCE_STATIC_ADMISSION_PARAGRAPH,
  type MaltaDistanceFeeLineKey,
  type MaltaDistanceFeeVisibility,
  type MaltaDistanceNoticeKey,
} from "@/src/constants/AdmissionOffice/Malta/maltaDistanceAdmissionConfig";
import {
  MALTA_FULL_TIME_NOTICE_ORDER,
  MALTA_FULL_TIME_NOTICE_TEXT,
} from "@/src/constants/AdmissionOffice/Malta/maltaFullTimeAdmissionConfig";
import { parseMaltaMoneyValue } from "@/src/utils/admissionOffice/malta/buildMaltaFullTimeAdmissionBody";
import { resolveIntakeAcademicYearLabel } from "@/src/utils/admissionOffice/malta/resolveIntakeAcademicYear";

const paragraph = (text: string) => `<p>${text}</p>`;
const strongParagraph = (text: string) => `<p><strong>${text}</strong></p>`;
const spacer = () => `<p>&nbsp;</p>`;

const buildFeeLine = (key: MaltaDistanceFeeLineKey, numericValue: string) => {
  const label = MALTA_DISTANCE_FEE_LINE_LABELS[key];
  const displayValue = formatMaltaDistanceFeeLineDisplay(key, numericValue);
  if (!displayValue) return "";
  return strongParagraph(`${label}: ${displayValue}`);
};

const buildTransferRequestParagraph = (completeOneYear?: string) => {
  const amount = completeOneYear?.trim();
  if (!amount) return "";
  return `<p style="text-align: center;"><span style="color: #ff0000;">You are requested to transfer the complete first year fee in the amount of ${amount} EUR into the bank account mentioned below:</span></p>`;
};

const buildBankDetailsSection = () =>
  [
    strongParagraph("NAME: FEU ADMISSION CENTER"),
    strongParagraph("IBAN: ES1521000639040201186222"),
    strongParagraph("BIC: CAIXESBBXXX"),
  ].join("\n");

export interface BuildMaltaDistanceAdmissionBodyParams {
  intake?: string;
  feeValues: Pick<Record<MaltaDistanceFeeLineKey, string | undefined>, MaltaDistanceFeeLineKey>;
  feeVisibility: MaltaDistanceFeeVisibility;
  completeOneYear?: string;
  noticeVisibility: Record<MaltaDistanceNoticeKey, boolean>;
}

export const calcMaltaDistanceFirstYearTotal = (
  feeValues: Pick<Record<MaltaDistanceFeeLineKey, string | undefined>, MaltaDistanceFeeLineKey>,
  feeVisibility: MaltaDistanceFeeVisibility,
) =>
  MALTA_DISTANCE_FEE_LINE_ORDER.reduce((total, key) => {
    if (!feeVisibility[key]) return total;
    return total + parseMaltaMoneyValue(feeValues[key]);
  }, 0);

export const buildMaltaDistanceAdmissionBody = ({
  intake,
  feeValues,
  feeVisibility,
  completeOneYear,
  noticeVisibility,
}: BuildMaltaDistanceAdmissionBodyParams): string => {
  const academicYear = resolveIntakeAcademicYearLabel(intake);
  const parts: string[] = [];

  parts.push(
    paragraph(
      `This letter is to confirm the admission of above named student in the course mentioned above for the ${academicYear} academic year.`,
    ),
  );
  parts.push(spacer());
  parts.push(paragraph("The fees are as following:"));

  MALTA_DISTANCE_FEE_LINE_ORDER.forEach((key) => {
    if (!feeVisibility[key]) return;
    const value = feeValues[key]?.trim();
    if (!value) return;
    const feeLine = buildFeeLine(key, value);
    if (feeLine) {
      parts.push(feeLine);
    }
  });

  parts.push(spacer());

  const transferParagraph = buildTransferRequestParagraph(completeOneYear);
  if (transferParagraph) {
    parts.push(transferParagraph);
    parts.push(spacer());
  }

  parts.push(buildBankDetailsSection());
  parts.push(spacer());
  parts.push(paragraph("Note! Groups will be created upon sufficient number of students."));
  parts.push(paragraph(MALTA_DISTANCE_STATIC_ADMISSION_PARAGRAPH));
  parts.push(spacer());

  let hasPreviousNotice = false;
  MALTA_FULL_TIME_NOTICE_ORDER.forEach((key) => {
    if (!noticeVisibility[key]) return;
    if (hasPreviousNotice) {
      parts.push(spacer());
    }
    parts.push(paragraph(MALTA_FULL_TIME_NOTICE_TEXT[key]));
    hasPreviousNotice = true;
  });

  return parts.join("\n");
};
